import { Storybook } from "@tento-ui/ui/storybook";
import { TurnTool, TurnToolGroup } from "./tool";

const CARGO_OUT = [
  "   Compiling tento-spool-server v0.1.0 (/Users/james/workspace/tento)",
  "    Finished `dev` profile [unoptimized + debuginfo] in 12.44s",
].join("\n");

const CHECK_OUT = [
  "error: TS2345 [ERROR]: Argument of type 'string' is not assignable to",
  "parameter of type 'HarnessView'.",
  "    return renderHarnessView(view.text);",
  "                             ~~~~~~~~~",
  "    at html/routes/stream-render.ts:90:30",
].join("\n");

/** Four claims, in order: the row is a sentence, its output hides behind
 * one chevron, nine vendor call types need no second primitive, and a run
 * of calls folds — except when one of them failed. */
export class TurnToolStoryBook extends Storybook.Default {
  title(): string {
    return "Turn · tool";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Every status, in the order a call moves through them",
        render: () => {
          const $wrap = column();
          $wrap.append(
            TurnTool.makeWith({
              tool: "read",
              title: "tento-ui-js/components/turn/tool.ts",
              status: "pending",
            }),
            TurnTool.makeWith({
              tool: "execute",
              title: "cargo build -p tento-spool-server",
              status: "running",
            }),
            TurnTool.makeWith({
              tool: "edit",
              title: "html/routes/stream-render.ts",
              note: "+12 −4",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "execute",
              title: "deno check html/routes/*.ts",
              note: "exit 1",
              status: "failed",
              detail: { text: CHECK_OUT },
            }),
            TurnTool.makeWith({
              tool: "write",
              title: "/etc/hosts",
              status: "declined",
            }),
          );
          return $wrap;
        },
      },
      {
        title: "The output is behind the chevron — closed, then open",
        render: () => {
          const $wrap = column();
          $wrap.append(
            TurnTool.makeWith({
              tool: "execute",
              verb: "Ran",
              title: "cargo build -p tento-spool-server",
              note: "exit 0",
              status: "done",
              detail: { text: CARGO_OUT },
            }),
            TurnTool.makeWith({
              tool: "execute",
              verb: "Ran",
              title: "cargo build -p tento-spool-server",
              note: "exit 0",
              status: "done",
              detail: { text: CARGO_OUT },
              expanded: true,
            }),
          );
          return $wrap;
        },
      },
      {
        title: "OpenAI's nine call types — one row, different words",
        render: () => {
          const $wrap = column();
          $wrap.append(
            TurnTool.makeWith({
              tool: "function",
              title: "get_weather({ city: \"Oakland\" })",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "shell",
              verb: "Ran",
              title: "git rev-list --count origin/master..master",
              note: "exit 0",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "web_search",
              verb: "Searched",
              title: "prosemirror gfm table schema",
              note: "6 sources",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "file_search",
              verb: "Searched files",
              title: "turn taxonomy revision",
              note: "3 hits",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "python",
              verb: "Analyzed",
              title: "df.groupby(\"messageId\").agg(len)",
              note: "320 rows",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "image",
              verb: "Generated",
              title: "a faint menu bar under a chat message, wireframe",
              note: "1024×1024",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "computer",
              verb: "Clicked",
              title: "(412, 288) — the details button",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "mcp · github",
              verb: "Called",
              title: "list_pull_requests",
              note: "14 open",
              status: "done",
            }),
            TurnTool.makeWith({
              tool: "custom",
              verb: "Invoked",
              title: "tento.spool.enqueue",
              status: "running",
            }),
          );
          return $wrap;
        },
      },
      {
        title: "A run of calls folds into one line",
        render: () =>
          TurnToolGroup.makeWith({
            tools: [
              {
                tool: "read",
                title: "tento-ui-js/components/turn/tool.ts",
                note: "165 lines",
                status: "done",
              },
              {
                tool: "read",
                title: "tento-ui-js/components/turn/note.ts",
                note: "165 lines",
                status: "done",
              },
              {
                tool: "grep",
                title: "face: \"meter\"",
                note: "1 hit",
                status: "done",
              },
              {
                tool: "edit",
                title: "html/routes/stream-render.ts",
                note: "+12 −4",
                status: "done",
              },
            ],
          }),
      },
      {
        title: "A dialect that knows what the run was says so",
        render: () =>
          TurnToolGroup.makeWith({
            summary: "Searched the web",
            tools: [
              {
                tool: "web_search",
                verb: "Searched",
                title: "acp permission option kinds",
                note: "6 sources",
                status: "done",
              },
              {
                tool: "web_search",
                verb: "Searched",
                title: "openai responses api call types",
                note: "9 sources",
                status: "done",
              },
            ],
          }),
      },
      {
        title: "A failure is never folded away — this one opens itself",
        render: () =>
          TurnToolGroup.makeWith({
            tools: [
              {
                tool: "read",
                title: "tento-spool/src/main.rs",
                note: "412 lines",
                status: "done",
              },
              {
                tool: "execute",
                verb: "Ran",
                title: "cargo build -p tento-spool",
                note: "exit 101",
                status: "failed",
                detail: { text: "error[E0432]: unresolved import `tento_env::EnvKv`" },
              },
              {
                tool: "write",
                title: "/etc/hosts",
                status: "declined",
              },
            ],
          }),
      },
      {
        title: "One call is a bare row — a disclosure over one line is ceremony",
        render: () =>
          TurnToolGroup.makeWith({
            tools: [
              {
                tool: "read",
                title: "tento-ui-js/components/turn/result.ts",
                note: "125 lines",
                status: "done",
              },
            ],
          }),
      },
      {
        title: "No label — a surface with one tool says so once, not per row",
        render: () =>
          TurnTool.makeWith({
            tool: "",
            title: "tento-node-registry/tento-spool.node/html/routes/index.root.ts",
            status: "done",
          }),
      },
      {
        title: "A long subject ellipsizes; the trailing fact never does",
        render: () =>
          TurnTool.makeWith({
            tool: "read",
            title:
              "/Users/james/workspace/tento/tento-node-registry/tento-spool.node/html/routes/session-detail.ts",
            note: "161 lines",
            status: "done",
          }),
      },
    ];
  }
}

function column(): HTMLElement {
  const $wrap = document.createElement("div");
  $wrap.style.display = "flex";
  $wrap.style.flexDirection = "column";
  $wrap.style.gap = "2px";
  $wrap.style.width = "100%";
  return $wrap;
}
